/**
 * Stores API Slice
 * Injects store endpoints into the base API slice
 */

import { apiSlice } from "../api/apiSlice";
import { Store } from "../../types/stores";
import { generateListCacheTag, CACHE_TIME } from "../api/apiHelpers";
import { mallsApiSlice } from "../malls/mallsApiSlice";

// Backend response structure for stores list
interface StoresBackendResponse {
  success: boolean;
  data: Store[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: {
    total: number;
    floors: string[];
    categories: string[];
  };
}

// Backend response for single store
interface StoreBackendResponse {
  success: boolean;
  data: Store;
}

// Backend response for global search
interface GlobalStoresSearchResponse {
  success: boolean;
  data: {
    stores: Store[];
    mallsCount: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Frontend expected structure
export interface StoresResponse {
  stores: Store[];
  total: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: {
    total: number;
    floors: string[];
    categories: string[];
  };
}

export interface StoreFiltersParams {
  mallId?: string;
  category?: string;
  floor?: string;
  sortBy?: string;
  searchQuery?: string;
  page?: number;
  limit?: number;
}

export const storesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get stores for a mall
     * Returns stores with optional filtering and sorting
     */
    getStoresByMall: builder.query<StoresResponse, StoreFiltersParams>({
      query: ({
        mallId,
        category,
        floor,
        sortBy,
        searchQuery,
        page = 1,
        limit = 10,
      }) => ({
        url: `/api/stores/mall/${mallId}`,
        params: {
          category: category !== "all" ? category : undefined,
          floor: floor !== "all" ? floor : undefined,
          sortBy,
          search: searchQuery,
          page,
          limit,
        },
      }),
      transformResponse: (response: StoresBackendResponse) => {
        return {
          stores: response.data || [],
          total: response.pagination?.total || 0,
          pagination: response.pagination,
          stats: response.stats,
        };
      },
      providesTags: (result, error, { mallId, category, floor, sortBy }) => [
        { type: "Stores", id: "LIST" },
        {
          type: "Stores",
          id: generateListCacheTag(
            `MALL-${mallId}`,
            category || "all",
            sortBy || "name",
          ),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.LONG, // Stores don't change frequently - 10 min cache
    }),

    /**
     * Get store by ID
     * Returns detailed information about a specific store
     */
    getStoreById: builder.query<Store, string>({
      query: (storeId) => ({
        url: `/api/store/${storeId}`,
      }),
      transformResponse: (response: StoreBackendResponse) => {
        return response.data;
      },
      async onCacheEntryAdded(
        storeId,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, getState },
      ) {
        try {
          // Wait for the data to be loaded
          const { data: store } = await cacheDataLoaded;

          // Update all getStoresByMall cache entries with the updated counts
          // Backend auto-increments view count, so we sync it to stores lists
          const state: any = getState();
          const cacheEntries = state.api?.queries || {};

          Object.keys(cacheEntries).forEach((cacheKey) => {
            if (cacheKey.startsWith("getStoresByMall")) {
              const entry = cacheEntries[cacheKey];
              const mallId = entry?.originalArgs?.mallId;

              if (mallId && store.mallId === mallId) {
                try {
                  dispatch(
                    storesApiSlice.util.updateQueryData(
                      "getStoresByMall",
                      entry.originalArgs,
                      (draft) => {
                        const cachedStore = draft.stores.find(
                          (s: Store) => s.id === storeId,
                        );
                        if (cachedStore) {
                          cachedStore.viewCount = store.viewCount;
                          cachedStore.shareCount = store.shareCount;
                        }
                      },
                    ),
                  );
                } catch (e) {
                  // Ignore individual cache update errors
                }
              }
            }
          });

          await cacheEntryRemoved;
        } catch {
          // Ignore errors during cache lifecycle
        }
      },
      providesTags: (result, error, storeId) => [
        { type: "Stores", id: storeId },
      ],
      keepUnusedDataFor: CACHE_TIME.LONG,
    }),

    /**
     * Global search across all malls
     * Search for stores by name across all malls
     */
    searchStoresGlobal: builder.query<StoresResponse, { searchQuery: string }>({
      query: ({ searchQuery }) => ({
        url: `/api/stores/search`,
        params: {
          search: searchQuery,
        },
      }),
      transformResponse: (response: GlobalStoresSearchResponse) => {
        return {
          stores: response.data?.stores || [],
          total: response.pagination?.total || 0,
        };
      },
      providesTags: ["Stores"],
      keepUnusedDataFor: CACHE_TIME.LONG,
    }),

    /**
     * Increment store view count
     * Called when user views store details
     */
    incrementStoreView: builder.mutation<
      void,
      { storeId: string; mallId: string }
    >({
      query: ({ storeId }) => ({
        url: `/api/store/${storeId}/view`,
        method: "POST",
      }),
      async onQueryStarted({ storeId, mallId }, { dispatch, queryFulfilled }) {
        // Optimistically update cache before API response
        const patchResult = dispatch(
          mallsApiSlice.util.updateQueryData("getMallHub", mallId, (draft) => {
            const mallHubDraft = draft as any;
            const store = mallHubDraft.stores?.find(
              (s: Store) => s.id === storeId,
            );
            if (store) {
              store.viewCount = (store.viewCount || 0) + 1;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          // Rollback optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { storeId }) => [
        { type: "Stores", id: storeId },
      ],
    }),

    // Increment store share count
    incrementStoreShare: builder.mutation<void, string>({
      query: (storeId) => ({
        url: `/api/store/${storeId}/share`,
        method: "POST",
      }),
      // Only invalidate list cache to update cards, not individual item to avoid refetch
      invalidatesTags: () => [{ type: "Stores", id: "LIST" }],
    }),

    /**
     * Get stores for admin (mall admin/owner)
     * Returns stores with Point of Contact (POC) information
     * Automatically filters by authenticated user's mall
     */
    getAdminStores: builder.query<StoresResponse, StoreFiltersParams>({
      query: ({
        category,
        floor,
        sortBy,
        searchQuery,
        page = 1,
        limit = 20,
      }) => ({
        url: `/api/admin/stores`,
        params: {
          category: category !== "all" ? category : undefined,
          floor: floor !== "all" ? floor : undefined,
          sortBy,
          search: searchQuery,
          page,
          limit,
        },
      }),
      transformResponse: (response: StoresBackendResponse) => {
        return {
          stores: response.data || [],
          total: response.pagination?.total || 0,
          pagination: response.pagination,
          stats: response.stats,
        };
      },
      providesTags: (result, error, { category, floor, sortBy }) => [
        { type: "Stores", id: "ADMIN-LIST" },
        {
          type: "Stores",
          id: generateListCacheTag(
            "ADMIN",
            category || "all",
            sortBy || "name",
          ),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.MEDIUM, // Admin data refreshes more frequently - 5 min cache
    }),
  }),
});

export const {
  useGetStoresByMallQuery,
  useGetStoreByIdQuery,
  useSearchStoresGlobalQuery,
  useIncrementStoreViewMutation,
  useIncrementStoreShareMutation,
  useGetAdminStoresQuery,
} = storesApiSlice;
